import type { Area, Config, Report, ReportType, Severity } from './types'
import { buildEnvironment, buildSourceHints, buildTarget } from './capture'

declare const __QCLENS_VERSION__: string
export const VERSION = typeof __QCLENS_VERSION__ !== 'undefined' ? __QCLENS_VERSION__ : '0.0.0-dev'

// Content stamp of the sources this bundle was built from — same source, same
// stamp. Exposed as QCLens.build so a page can be asked which bundle it is
// actually running (the answer to "I hard-reloaded and nothing changed").
declare const __QCLENS_BUILD__: string
export const BUILD = typeof __QCLENS_BUILD__ !== 'undefined' ? __QCLENS_BUILD__ : 'dev'

function uuid(): string {
  if (crypto.randomUUID) return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export interface ExperienceInput {
  description: string
  expected?: string
  report_type: ReportType
  area: Area
  severity?: Severity
  severity_touched?: boolean
}

/** Build the experience block, deriving legacy `category` = area for downstream consumers. */
export function buildExperience(input: ExperienceInput): Report['experience'] {
  return {
    description: input.description,
    report_type: input.report_type,
    area: input.area,
    category: input.area, // legacy bridge: category == area (visual/ux/content/performance/functional)
    ...(input.expected ? { expected: input.expected } : {}),
    ...(input.severity ? { severity: input.severity } : {}),
    ...(input.severity_touched !== undefined ? { severity_touched: input.severity_touched } : {}),
  }
}

export function assembleReport(el: Element, experience: ExperienceInput, config: Config): Report {
  const report: Report = {
    qclens_version: VERSION,
    report_id: uuid(),
    created_at: new Date().toISOString(),
    experience: buildExperience(experience),
    target: buildTarget(el, config.redact ?? [], config.captureStyles !== false),
    source_hints: runEnrichers(el, buildSourceHints(el) ?? {}, config),
    environment: buildEnvironment(),
  }
  if (config.reporter) report.reporter = config.reporter
  return report
}

/** Merge adapter enrichers over the base DOM-derived source hints. */
export function runEnrichers(el: Element, base: NonNullable<Report['source_hints']>, config: Config) {
  const enrichers = config.enrich ? (Array.isArray(config.enrich) ? config.enrich : [config.enrich]) : []
  let hints = base
  for (const fn of enrichers) {
    try {
      const extra = fn(el, hints)
      if (extra) hints = { ...hints, ...extra }
    } catch (err) {
      // An adapter enricher must never block report assembly.
      console.warn('[qc-lens] enricher failed:', err)
    }
  }
  return hints
}
